import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, input, output, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CouponService,
  ExcelService,
  PlanmasterService,
} from '@fitness-force/api';
import type {
  AddServiceCategoryRequest,
  CouponServicesResponse,
  OfferMaster,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ScrollPanelModule } from 'primeng/scrollpanel';

import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'coupon-plan-restriction',
  templateUrl: './coupon-plan-restriction.component.html',
  standalone: true,
  imports: [
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollPanelModule,
    ButtonModule,
    SelectModule,
    TranslateModule,
    AsyncPipe,
    NgTemplateOutlet,
  ],
})
export class CouponPlanRestrictionComponent implements OnInit {
  private coupnService = inject(CouponService);
  private translate = inject(TranslateService);
  private excelService = inject(ExcelService);
  private toastMessageService = inject(ToastMessageService);
  private planService = inject(PlanmasterService);

  readonly tenantId = input(0);
  readonly couponMaster = input<OfferMaster>();
  readonly visible = input<boolean>(false);
  readonly planIds = input<number[]>([]);
  readonly closeEvent = output<any>({ alias: 'closeEvent' });

  plans: any[] = [];
  couponPlans: CouponServicesResponse[] = [];
  selectedPlans = new BehaviorSubject<CouponServicesResponse[]>([]);

  reloadList: boolean = false;

  ngOnInit(): void {
    this.BindPlans();
  }

  BindPlans() {
    this.planService.getSubscriptionPlanService(this.tenantId()).subscribe({
      next: (res: any) => {
        if (res.items) this.plans = res.items;
        else this.plans = [];
        this.getCouponEligiblePlans();
      },
      error: (error: any) => {},
    });
  }

  getCouponEligiblePlans() {
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .getEligibleService(this.tenantId(), couponMaster.id)
      .subscribe({
        next: (res: any) => {
          if (res.items) {
            this.couponPlans = res.items as CouponServicesResponse[];
            this.couponPlans.forEach((o) => {
              const plan = this.plans.find((f) => f.id == o.plan_id);
              o.service_category_name =
                plan.name_primary_language ||
                plan.name_secondary_language ||
                '';
            });
            this.selectedPlans.next(this.couponPlans);
          } else {
            this.selectedPlans.next([]);
            this.couponPlans = [];
          }
        },
        error: (error: any) => {},
      });
  }

  addService(event: any) {
    let payload: AddServiceCategoryRequest = {} as AddServiceCategoryRequest;
    payload.service_category_ids = [];
    payload.product_sku_id = [];
    payload.plan_ids = [];
    payload.plan_ids.push(event.value.id);
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .addEligibleService(this.tenantId(), couponMaster.id, payload)
      .subscribe({
        next: (res: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.SERVICE_ADDED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.getCouponEligiblePlans();
          this.reloadList = true;
        },
        error: (error: any) => {
          this.handleToastError(error);
        },
      });
  }

  removeService(couponServicesResponse: CouponServicesResponse) {
    if (this.couponPlans && this.couponPlans.length == 1) {
      this.translate
        .get('ADMIN.COUPON.ERRORS.ONE_PLAN_REQUIRED')
        .subscribe((translation) => {
          this.toastMessageService.showErrorMessage(translation);
        });
    } else {
      this.coupnService
        .removeEligibleService(
          this.tenantId(),
          couponServicesResponse.offer_master_id,
          couponServicesResponse.id,
        )
        .subscribe({
          next: (res: any) => {
            this.translate
              .get('ADMIN.COUPON.ERRORS.SERVICE_REMOVED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.getCouponEligiblePlans();
            this.reloadList = true;
          },
          error: (error: any) => {
            this.handleToastError(error);
          },
        });
    }
  }

  handleToastError(error: any) {
    this.toastMessageService.showError(error);
  }

  closePopup() {
    this.closeEvent.emit({ reload: this.reloadList });
    this.closeEvent.emit({ reload: this.reloadList });
  }
}
